import { supabase } from './supabase';

/** Shown when a username is already registered (case-insensitive via stored lowercase). */
export const USERNAME_TAKEN_MESSAGE = 'This username is already taken. Try another.';

export function normalizeUsername(raw: string): string {
  return raw.trim().toLowerCase();
}

/** Returns an error message if invalid, or null if OK. */
export function validateUsernameFormat(normalized: string): string | null {
  if (normalized.length < 3) return 'Username must be at least 3 characters.';
  if (normalized.length > 30) return 'Username must be at most 30 characters.';
  if (!/^[a-z0-9_]+$/.test(normalized)) {
    return 'Username can only contain lowercase letters, numbers, and underscores.';
  }
  return null;
}

/**
 * True if another profile already uses this username (case-insensitive via DB RPC).
 */
export async function isUsernameTaken(username: string, excludeUserId?: string): Promise<boolean> {
  const u = normalizeUsername(username);
  if (u.length < 3) return true;
  const { data: taken, error } = await supabase.rpc('username_taken', {
    p_username: u,
    p_exclude: excludeUserId ?? null,
  });
  if (!error && typeof taken === 'boolean') return taken;
  let q = supabase.from('profiles').select('id').eq('username', u);
  if (excludeUserId) q = q.neq('id', excludeUserId);
  const { data, error: qErr } = await q.limit(1).maybeSingle();
  if (qErr) return true;
  return !!data;
}

export function mapProfileUsernameError(err: { message?: string; code?: string } | null): string | null {
  if (!err) return null;
  const msg = (err.message || '').toLowerCase();
  const code = err.code || '';
  if (code === '23505' && (msg.includes('username') || msg.includes('profiles_username'))) {
    return USERNAME_TAKEN_MESSAGE;
  }
  if (msg.includes('duplicate') && msg.includes('username')) return USERNAME_TAKEN_MESSAGE;
  return null;
}
